﻿import React from "react";
import { CustomerListModel, PlanStatus } from "./Customers.types";
import { Box, TableCell } from "@mui/material";
import TableRow from "@common/components/tableRow/TableRow";
import { DotLegalButton, DotLegalOverflowMenu, DotLegalPageHeader, DotLegalTableWithControls, ITableHeader } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "../../localization/useTranslation";
import { useDataMapping } from "./Customers.hooks";
import AddEditCustomerDialog from "../addEditCustomerDialog/AddEditCustomerDialog";
import { useUserContext } from "../../auth/userContextProvider/UserContextProvider";
import { DeleteCustomerDialog } from "../deleteCustomerDialog/DeleteCustomerDialog";
import CheckIcon from "@mui/icons-material/Check";
import { useCustomersStyle } from "./Customers.styles";
import { getLoginProviderName } from "../../user/User.types";
import { CustomerSaveModel } from "../addEditCustomerDialog/AddEditCustomer.types";
import { useNavigate } from "react-router";
import { useUrlProvider } from "../../useUrlProvider";
import { PlanType } from "../../auth/planProvider/PlanProvider.types";
import PageHeaderEditEntityButton from "../../common/components/pageHeaderEditEntityButton/PageHeaderEditEntityButton.tsx";
import TablePagePaper from "@common/components/tablePagePaper/TablePagePaper";

export interface ICustomersProps {}

function Customers(props: ICustomersProps) {
    const styles = useCustomersStyle();
    const { permissions, customerName } = useUserContext();
    const { translateString, translateLanguage, translateDate } = useTranslation();
    const navigate = useNavigate();
    const urlProvider = useUrlProvider();

    const {
        customers,
        isLoading,
        refetch,
        showDeleteCustomerDialog,
        setShowDeleteCustomerDialog,
        selectedCustomer,
        setSelectedCustomer,
        showAddEditDialog,
        setShowAddEditDialog,
        createNewCustomerProps,
        getBreadCrumbs,
    } = useDataMapping();

    function mapToSaveModel(customer: CustomerListModel): CustomerSaveModel {
        return {
            id: customer.id,
            name: customer.name,
            language: customer.language,
            active: customer.active,
            loginProvider: customer.loginProvider,
            helpLink: customer.helpLink,
            planType: customer.planType,
            reportTypes: customer.reportTypes,
            germanEnabled: customer.germanEnabled,
        };
    }

    function getCustomerTypeName(customerType: PlanStatus) {
        switch (customerType) {
            case PlanStatus.Trial:
                return "Trial";
            case PlanStatus.Live:
                return "Live";
            case PlanStatus.Demo:
                return "Demo";
            default:
                return "";
        }
    }

    function getPlanTypeName(planType: PlanType) {
        switch (planType) {
            case PlanType.DotLegalCompliance:
                return "Compliance Platform";
            case PlanType.Research:
                return "Research";
            default:
                return "";
        }
    }

    const getMenuItems = (customer: CustomerListModel) => {
        let menuItems = [];
        if (permissions.canManageCustomers) {
            menuItems.push({
                menuItem: translateString("edit"),
                onClick: () => {
                    navigate(urlProvider.getAddEditCustomerDialog(customer.id));
                },
            });

            menuItems.push({
                menuItem: translateString("removeCustomer"),
                onClick: () => {
                    setSelectedCustomer(mapToSaveModel(customer));
                    setShowDeleteCustomerDialog(true);
                },
            });
        }
        return menuItems;
    };

    return (
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
            <DotLegalPageHeader
                breadCrumbs={getBreadCrumbs()}
                rightComponent={
                    <DotLegalButton
                        buttonType="primary"
                        size="medium"
                        onClick={() => {
                            setSelectedCustomer({ ...createNewCustomerProps });
                            setShowAddEditDialog(true);
                        }}
                    >
                        {translateString("createCustomer")}
                    </DotLegalButton>
                }
            />

            <TablePagePaper>
                <DotLegalTableWithControls
                    internalScroll
                    hasOverflowMenu
                    headers={getHeaders(translateString)}
                    getUserSpecificPageLength={() => 30}
                    hideRowsPerPage
                    defaultOrderBy={"name"}
                    defaultOrder={"asc"}
                    clickableRows={false}
                    isLoading={isLoading}
                    labelRowsPerPage={translateString("showEntites")}
                    labelSearch={translateString("search")}
                    noOptionsLabel={translateString("noOptions")}
                    paginationLabelOf={translateString("labelOf")}
                    emptyText={translateString("noData")}
                    data={customers}
                    renderRow={(row, i) => (
                        <TableRow key={i} inactive={!row.active} inactiveTooltip={translateString("deactivated")}>
                            <TableCell>{row.name}</TableCell>
                            <TableCell>{translateLanguage(row.language)}</TableCell>
                            <TableCell>{getLoginProviderName(row.loginProvider)}</TableCell>
                            <TableCell>
                                <Box sx={styles.iconContainer}>{row.additionalProcessingActivityReports ? <CheckIcon sx={styles.checkIcon} /> : ""}</Box>
                            </TableCell>
                            <TableCell>
                                <Box sx={styles.iconContainer}>{row.active ? <CheckIcon sx={styles.checkIcon} /> : ""}</Box>
                            </TableCell>
                            <TableCell>{getCustomerTypeName(row.planStatus)}</TableCell>
                            <TableCell>{getPlanTypeName(row.planType)}</TableCell>
                            <TableCell>{translateDate(row.created)}</TableCell>
                            <TableCell>{row.endDate ? translateDate(row.endDate!) : ""}</TableCell>
                            <TableCell align={"right"}>
                                <DotLegalOverflowMenu items={getMenuItems(row)} />
                            </TableCell>
                        </TableRow>
                    )}
                />
            </TablePagePaper>

            {showAddEditDialog && (
                <AddEditCustomerDialog
                    onCloseDialog={() => setShowAddEditDialog(false)}
                    onSave={async () => {
                        setShowAddEditDialog(false);
                        await refetch();
                    }}
                    selectedCustomer={selectedCustomer}
                />
            )}

            {showDeleteCustomerDialog && (
                <DeleteCustomerDialog
                    selectedCustomer={selectedCustomer!}
                    onDeletion={async () => {
                        await refetch();
                    }}
                    onCloseDialog={() => setShowDeleteCustomerDialog(false)}
                />
            )}
        </Box>
    );

    function getHeaders(translateString: any) {
        let headers = Array<ITableHeader<CustomerListModel>>();
        headers.push({ property: "name", text: translateString("name"), align: "left", showOnMobile: true });
        headers.push({ property: "language", text: translateString("language"), align: "left", showOnMobile: true });
        headers.push({ property: "loginProvider", text: translateString("loginMethod"), align: "left", showOnMobile: true });
        headers.push({
            property: "additionalProcessingActivityReports",
            text: translateString("additionalProcessingActivityReports"),
            align: "left",
            showOnMobile: true,
        });
        headers.push({ property: "active", text: translateString("active"), align: "left", showOnMobile: true });
        headers.push({ property: "planStatus", text: "Plan Status", align: "left", showOnMobile: true });
        headers.push({ property: "planType", text: "Core Plan", align: "left", showOnMobile: true });
        headers.push({ property: "created", text: translateString("created"), align: "left", showOnMobile: true });
        headers.push({ property: "endDate", text: translateString("endDate"), align: "left", showOnMobile: true });

        return headers;
    }
}

export default Customers;
